import pipefitterApi from '../../../services/pipefitterApi';
import type { PipeStatusItem } from '../types';
import { extractPipeStatusListRows, isGetPipeStatusListSuccess } from './pipeStatusList';

/**
 * Lookup a single pipe end in the project database.
 *
 * Backend contract (to be implemented):
 *   GET /reports/getPipeStatusByPipeId
 *     ?project_num={projectNumber}
 *     &pipe_id={materialPipeId}      // pipe size / material line id
 *     &pipeID={pipeEndId}            // individual pipe identifier to find
 *     [&project_id=...]
 *     [&project_name=...]
 *
 * Success when found:
 *   { status: 200, data: { id, pipeID, status, spread, pipeData, ... } }
 *
 * Not found:
 *   { status: 200, data: null } | { status: 404, message: "..." } | { status: 200, data: [] }
 */
export interface LookupPipeStatusByPipeIdParams {
  projectNumber: string;
  projectId?: string | number | null;
  /** Material line pipe id (same as getPipeStatusList `pipe_id`). */
  materialPipeId: string;
  /** Individual pipe end id shown in the table `pipeID` column. */
  pipeID: string;
  projectName?: string;
}

export interface LookupPipeStatusByPipeIdResult {
  exists: boolean;
  pipe: PipeStatusItem | null;
}

export function buildLookupPipeStatusQuery(params: LookupPipeStatusByPipeIdParams): URLSearchParams {
  const query = new URLSearchParams();
  query.set('project_num', params.projectNumber.trim());
  query.set('pipe_id', params.materialPipeId.trim());
  query.set('pipeID', params.pipeID.trim());
  query.set('deleted', '0');
  if (params.projectId != null && params.projectId !== '') {
    query.set('project_id', String(params.projectId));
  }
  if (params.projectName?.trim()) {
    query.set('project_name', params.projectName.trim());
  }
  return query;
}

export function getLookupPipeStatusByPipeIdUrl(query: URLSearchParams): string {
  return `/reports/getPipeStatusByPipeId?${query.toString()}`;
}

export function parseLookupPipeStatusResponse(body: unknown): LookupPipeStatusByPipeIdResult {
  if (!body || typeof body !== 'object') {
    return { exists: false, pipe: null };
  }

  const envelope = body as Record<string, unknown>;

  if (envelope.status === 404 || envelope.success === 0 || envelope.success === false) {
    return { exists: false, pipe: null };
  }

  const data = envelope.data;

  if (data == null) {
    return { exists: false, pipe: null };
  }

  if (Array.isArray(data)) {
    const first = data[0];
    if (first && typeof first === 'object') {
      return { exists: true, pipe: first as PipeStatusItem };
    }
    return { exists: false, pipe: null };
  }

  if (typeof data === 'object') {
    const record = data as Record<string, unknown>;
    const nestedRows = extractPipeStatusListRows(data);
    if (nestedRows.length > 0) {
      return { exists: true, pipe: nestedRows[0] };
    }
    if (record.pipeID != null || record.id != null || record.pipeStatusId != null) {
      return { exists: true, pipe: record as unknown as PipeStatusItem };
    }
  }

  return { exists: false, pipe: null };
}

export async function lookupPipeStatusByPipeId(
  params: LookupPipeStatusByPipeIdParams
): Promise<LookupPipeStatusByPipeIdResult> {
  const pipeID = params.pipeID?.trim();
  const projectNumber = params.projectNumber?.trim();
  const materialPipeId = params.materialPipeId?.trim();

  if (!pipeID || !projectNumber || !materialPipeId) {
    return { exists: false, pipe: null };
  }

  try {
    const query = buildLookupPipeStatusQuery({ ...params, pipeID, projectNumber, materialPipeId });
    const { data } = await pipefitterApi.get(getLookupPipeStatusByPipeIdUrl(query));

    if (!isGetPipeStatusListSuccess(data) && !parseLookupPipeStatusResponse(data).exists) {
      return { exists: false, pipe: null };
    }

    return parseLookupPipeStatusResponse(data);
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 404) {
      return { exists: false, pipe: null };
    }
    throw error;
  }
}
